'use client';

import * as AvatarPrimitive from '@radix-ui/react-avatar';
import * as React from 'react';

import { cn } from '@/lib/utils';

// The Root is NOT overflow-hidden: an AvatarBadge sits at the corner and would
// otherwise be clipped. The circular clip lives on the Image/Fallback instead
// (each rounded-full + overflow-hidden), so every avatar is a proper circle
// regardless of what it contains (photo, initials, or an SVG mark like @Lex).
const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn('relative flex h-10 w-10 shrink-0 rounded-full', className)}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn(
      'aspect-square h-full w-full overflow-hidden rounded-full',
      className
    )}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      'flex h-full w-full items-center justify-center overflow-hidden rounded-full',
      className
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

/**
 * A small status dot anchored to the avatar's bottom-right corner (shadcn "base"
 * Avatar parity). Defaults to the semantic success (online) color; the ring
 * matches the surface so the dot reads as a cutout. Override `className` for a
 * different state color.
 */
const AvatarBadge = React.forwardRef<
  HTMLSpanElement,
  React.HTMLAttributes<HTMLSpanElement>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(
      'absolute bottom-0 right-0 block h-2.5 w-2.5 rounded-full ring-2 ring-surface-base bg-status-success',
      className
    )}
    {...props}
  />
));
AvatarBadge.displayName = 'AvatarBadge';

export { Avatar, AvatarImage, AvatarFallback, AvatarBadge };
